import type { Membership } from '../api/schemas';
import { formatWholeDollars } from '../components/PlanPicker';
import { formatMoneyCents } from './format';
import {
  ANNUAL_DISCOUNT_PCT,
  annualPriceCents,
  planHasAnnualBilling,
  planLabel,
  RESERVAS_FREE_BOOKINGS_PER_MONTH,
  type BillingCycle,
  type PlanTier,
} from './plan';

/**
 * Reading a membership's prices the same way on every owner screen: the
 * server's own figures first, computed identically for an API that predates
 * annual billing.
 */

export function annualDiscountPctOf(membership: Membership): number {
  return membership.annualDiscountPct ?? ANNUAL_DISCOUNT_PCT;
}

/** What `plan` costs this business a month, its negotiated discount included. */
export function monthlyPriceOf(membership: Membership, plan: PlanTier): number {
  return membership.plans.find((entry) => entry.id === plan)?.priceCents ?? 0;
}

/** Twelve months of that price paid up front; null for a plan with no monthly fee. */
export function annualPriceOf(membership: Membership, plan: PlanTier): number | null {
  if (!planHasAnnualBilling(plan)) return null;
  const entry = membership.plans.find((option) => option.id === plan);
  return entry?.annualPriceCents ?? annualPriceCents(monthlyPriceOf(membership, plan), annualDiscountPctOf(membership));
}

/** "$50 al mes" or "$480 al año", whichever the business would be billed. */
export function cyclePriceText(membership: Membership, plan: PlanTier, cycle: BillingCycle): string {
  const yearly = cycle === 'annual' ? annualPriceOf(membership, plan) : null;
  if (yearly === null) return `${formatWholeDollars(monthlyPriceOf(membership, plan))} al mes`;
  return `${formatWholeDollars(yearly)} al año`;
}

/** "Pro, pago anual: $480 al año", or "Independientes: 10 reservas gratis al mes, luego $0.50 cada una". */
export function planOnFileLine(membership: Membership): string {
  const label = planLabel(membership.plan);
  if (!planHasAnnualBilling(membership.plan)) {
    return `${label}: ${RESERVAS_FREE_BOOKINGS_PER_MONTH} reservas gratis al mes, luego ${formatMoneyCents(membership.perBookingCents)} cada una`;
  }
  const cycleWord = membership.billingCycle === 'annual' ? 'pago anual' : 'pago mensual';
  return `${label}, ${cycleWord}: ${cyclePriceText(membership, membership.plan, membership.billingCycle)}`;
}
